// Authentication Manager for user login, registration, and session management
export class AuthManager {
  static initialize() {
    this.currentUser = null;
    this.sessionToken = localStorage.getItem('auth_token');
    this.isInitialized = true;
    
    // Check if user is already logged in
    if (this.sessionToken) {
      this.validateSession();
    }
    
    console.log('AuthManager initialized');
  }

  // Register a new user
  static async register(userData) {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      const result = await response.json();
      
      if (result.success) {
        this.currentUser = result.user;
        this.sessionToken = result.token;
        localStorage.setItem('auth_token', this.sessionToken);
        
        // Track registration
        if (window.AnalyticsManager) {
          window.AnalyticsManager.trackEvent('user_registration', {
            userId: result.user.id,
            email: result.user.email
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login user
  static async login(email, password) {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const result = await response.json();
      
      if (result.success) {
        this.currentUser = result.user;
        this.sessionToken = result.token;
        localStorage.setItem('auth_token', this.sessionToken);
        
        // Track login
        if (window.AnalyticsManager) {
          window.AnalyticsManager.trackEvent('user_login', {
            userId: result.user.id,
            email: result.user.email
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout user
  static async logout() {
    try {
      if (this.sessionToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.sessionToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.currentUser = null;
      this.sessionToken = null;
      localStorage.removeItem('auth_token');
      
      // Track logout
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('user_logout');
      }
    }
  }

  // Validate current session
  static async validateSession() {
    if (!this.sessionToken) return false;

    try {
      const response = await fetch('/api/auth/validate', {
        headers: {
          'Authorization': `Bearer ${this.sessionToken}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        this.currentUser = result.user;
        return true;
      } else {
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Session validation error:', error);
      this.logout();
      return false;
    }
  }

  // Get current user
  static getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.currentUser && !!this.sessionToken;
  }

  // Get session token
  static getSessionToken() {
    return this.sessionToken;
  }

  // Update user profile
  static async updateProfile(userData) {
    if (!this.isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.sessionToken}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('Profile update failed');
      }

      const result = await response.json();
      
      if (result.success) {
        this.currentUser = result.user;
        
        // Track profile update
        if (window.AnalyticsManager) {
          window.AnalyticsManager.trackEvent('profile_update', {
            userId: this.currentUser.id
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  // Change password
  static async changePassword(currentPassword, newPassword) {
    if (!this.isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.sessionToken}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        throw new Error('Password change failed');
      }

      const result = await response.json();
      
      if (result.success) {
        // Track password change
        if (window.AnalyticsManager) {
          window.AnalyticsManager.trackEvent('password_change', {
            userId: this.currentUser.id
          });
        }
      }

      return result;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }
}